import React, { useState, useEffect } from "react"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import {
  Box,
  Typography,
  Paper,
  Grid,
  Card,
  CardContent,
  TextField,
  MenuItem,
  Divider,
  Tooltip,
  IconButton,
  Tabs,
  Tab,
  Slider,
  LinearProgress,
  Alert,
  CircularProgress,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
} from "@mui/material"
import {
  HowToVote,
  ThumbsUpDown,
  WarningAmber,
  Info as InfoIcon,
  Dashboard as DashboardIcon,
  TrackChanges,
  OnlinePrediction,
  Campaign,
  Psychology,
  Refresh,
  Gavel,
  LocalShipping,
  GroupAdd,
  RecordVoiceOver,
} from "@mui/icons-material"
import { PieChart } from "@mui/x-charts/PieChart"
import { BarChart } from "@mui/x-charts/BarChart"
import dayjs from "dayjs"

import Header from "../../layouts/Header"
import BackIcon from "../../components/BackIcon"
import CustomBackdrop from "../../components/CustomBackdrop"
import { useQuery, api } from "../../api"

const StyledRoot = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(3),
  flexGrow: 1,
  padding: "0 16px",
  paddingBottom: "40px",
}))

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#2E7D32" }, error: { main: "#d32f2f" } },
})

const KPICard = ({ title, value, icon, color, subtitle, strategyText }) => (
  <Card elevation={2} sx={{ height: "100%", borderLeft: `6px solid ${color}`, borderRadius: 2 }}>
    <CardContent sx={{ p: 2, "&:last-child": { pb: 2 } }}>
      <Box display="flex" justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Box display="flex" alignItems="center">
            <Typography
              variant="body2"
              color="textSecondary"
              fontWeight="bold"
              sx={{ textTransform: "uppercase", mb: 1 }}
            >
              {title}
            </Typography>
            {strategyText && (
              <Tooltip title={<Typography fontSize={13}>{strategyText}</Typography>} placement="top" arrow>
                <IconButton size="small" sx={{ ml: 0.5, mt: -1, p: 0 }}>
                  <InfoIcon fontSize="small" color="primary" />
                </IconButton>
              </Tooltip>
            )}
          </Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color }}>
            {value}
          </Typography>
          {subtitle && (
            <Typography variant="caption" color="textSecondary" sx={{ mt: 1, display: "block", fontWeight: "500" }}>
              {subtitle}
            </Typography>
          )}
        </Box>
        <Box sx={{ bgcolor: `${color}1A`, p: 1.5, borderRadius: "50%", display: "flex" }}>
          {React.cloneElement(icon, { sx: { color, fontSize: 32 } })}
        </Box>
      </Box>
    </CardContent>
  </Card>
)

const DashboardPoliticoIndex = () => {
  const [activeTab, setActiveTab] = useState(0)
  const [zonaFiltro, setZonaFiltro] = useState("TODOS")

  // ESTADOS DEL SIMULADOR - FASE 2
  const [simCanton, setSimCanton] = useState("Daule")
  const [simRecall, setSimRecall] = useState(0)
  const [simAntivoto, setSimAntivoto] = useState(0)
  const [simVotoFirme, setSimVotoFirme] = useState(25)
  const [simCanal, setSimCanal] = useState("Redes Sociales")

  // OBTENCIÓN DE DATOS CON AUTO-REFRESCO
  const {
    data: metrics,
    isLoading,
    isFetching,
    refetch,
    dataUpdatedAt,
  } = useQuery({
    queryKey: ["getDashboardPoliticoMetrics", zonaFiltro],
    queryFn: async () => {
      const payload = { zoncodigo: zonaFiltro === "TODOS" ? null : zonaFiltro }
      const res = await api.post("/EjecEncuestas/getDashboardPolitico", payload)
      return res?.data?.success ? res.data.data : null
    },
    refetchOnWindowFocus: true,
    refetchInterval: 60000,
  })

  useEffect(() => {
    if (metrics) {
      if (metrics.tasaPenetracion !== undefined) setSimRecall(metrics.tasaPenetracion)
      if (metrics.pctAntivoto !== undefined) setSimAntivoto(metrics.pctAntivoto)

      if (metrics.firmezaVoto && metrics.firmezaVoto.length > 0) {
        const votoDuro = metrics.firmezaVoto.find(
          (v) => v.label.toUpperCase().includes("DURO") || v.label.toUpperCase().includes("FIRME"),
        )
        if (votoDuro) setSimVotoFirme(votoDuro.value)
      }
    }
  }, [metrics])

  const techoElectoral = Math.max(0, 100 - simAntivoto)
  const persuabilidad = Math.max(0, 100 - simVotoFirme)

  let factorCanal = 1.0
  if (simCanal === "Recorridos de Calle") factorCanal = 1.3
  if (simCanal === "Boca a Boca") factorCanal = 1.2
  if (simCanal === "Redes Sociales") factorCanal = 1.0
  if (simCanal === "Radio Local") factorCanal = 0.8
  const eficienciaCampana = Math.min(100, Math.round(simRecall * factorCanal))

  const data = metrics || {}
  const intencionData = data.intencionVoto || []
  const doloresData = data.doloresCiudadanos || []
  const mixMediosData = data.mixMedios || []
  const firmezaData = data.firmezaVoto || []

  // DATOS FASE 3 (Desestructuración defensiva por si el backend aún no los manda)
  const fase3 = data.fase3 || {}
  const crmPoliticoData = fase3.crmPolitico || []
  const logisticaData = fase3.logistica || []
  const controlData = fase3.controlElectoral || []
  const microData = fase3.microNecesidades || []

  const ultimaActualizacion = dataUpdatedAt ? dayjs(dataUpdatedAt).format("DD/MM/YYYY HH:mm:ss") : "Esperando..."

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", mb: 1, color: "#196C87" }}>
          <DashboardIcon sx={{ fontSize: 36, mr: 1 }} />
          <Typography variant="h4" fontWeight="bold">
            Inteligencia Política y Electoral
          </Typography>
        </Box>
        <Typography variant="body1" align="center" color="textSecondary" mb={3}>
          SIAC Web Analytics - Módulo Avanzado de Campaña 2026
        </Typography>

        <CustomBackdrop isLoading={isLoading} />

        <StyledRoot>
          <Paper elevation={1} sx={{ mb: 3, borderRadius: 2 }}>
            <Tabs
              value={activeTab}
              onChange={(e, newValue) => setActiveTab(newValue)}
              indicatorColor="primary"
              textColor="primary"
              variant="scrollable"
              scrollButtons="auto"
              sx={{ borderBottom: 1, borderColor: "divider" }}
            >
              <Tab icon={<DashboardIcon />} label="FASE 1: DIAGNÓSTICO" sx={{ fontWeight: "bold" }} />
              <Tab icon={<TrackChanges />} label="FASE 2: TRACKING Y SIMULADOR" sx={{ fontWeight: "bold" }} />
              <Tab icon={<Gavel />} label="FASE 3: WAR ROOM TÁCTICO" sx={{ fontWeight: "bold" }} />
            </Tabs>
          </Paper>

          {/* ========================================================= */}
          {/* BARRA SUPERIOR DE AUTO-REFRESCO (COMPARTIDA)              */}
          {/* ========================================================= */}
          <Paper elevation={1} sx={{ p: 2, mb: 4, borderRadius: 2, borderTop: "4px solid #196C87" }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  select
                  fullWidth
                  label="Filtrar por Zona / Cantón"
                  value={zonaFiltro}
                  onChange={(e) => setZonaFiltro(e.target.value)}
                  size="small"
                >
                  <MenuItem value="TODOS">Consolidado General</MenuItem>
                  <MenuItem value="SUC">Cantón Sucre (Bahía de Caráquez)</MenuItem>
                  <MenuItem value="LEO">Parroquia Leonidas Plaza</MenuItem>
                </TextField>
              </Grid>

              <Grid item xs={12} sm={6} md={8}>
                <Box display="flex" justifyContent={{ xs: "flex-start", sm: "flex-end" }} alignItems="center">
                  {isFetching && !isLoading && <CircularProgress size={16} sx={{ mr: 1.5, color: "#196C87" }} />}
                  <Typography variant="body2" color="textSecondary" sx={{ mr: 1 }}>
                    Último barrido: <b>{ultimaActualizacion}</b> (Auto-refresco 60s)
                  </Typography>
                  <Tooltip title="Recargar datos ahora">
                    <span>
                      <IconButton
                        onClick={() => refetch()}
                        color="primary"
                        disabled={isFetching}
                        size="small"
                        sx={{ border: "1px solid #196C87" }}
                      >
                        <Refresh fontSize="small" />
                      </IconButton>
                    </span>
                  </Tooltip>
                </Box>
              </Grid>
            </Grid>
          </Paper>

          {/* ========================================================= */}
          {/* FASE 1: DIAGNÓSTICO                                       */}
          {/* ========================================================= */}
          {activeTab === 0 && (
            <Box>
              <Grid container spacing={3} mb={4} justifyContent="center">
                <Grid item xs={12} sm={4}>
                  <KPICard
                    title="Muestra Electoral"
                    value={data.totalEncuestados || 0}
                    icon={<HowToVote />}
                    color="#196C87"
                    subtitle="Total de encuestas ejecutadas"
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <KPICard
                    title="NPS Político"
                    value={`${(data.npsPolitico || 0) > 0 ? "+" : ""}${data.npsPolitico || 0}`}
                    icon={<ThumbsUpDown />}
                    color={(data.npsPolitico || 0) >= 0 ? "#2E7D32" : "#d32f2f"}
                    subtitle="Índice de Favorabilidad Neta"
                    strategyText="Si es negativo, limpie imagen antes de proponer. Si es positivo, ataque y proponga."
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <KPICard
                    title="Desgaste del Oficialismo"
                    value={`${data.desgasteOficialismo || 0}%`}
                    icon={<WarningAmber />}
                    color={(data.desgasteOficialismo || 0) > 55 ? "#ed6c02" : "#196C87"}
                    subtitle="Tasa de rechazo a la gestión actual"
                    strategyText="> 55% indica que la campaña debe enfocarse en la palabra 'CAMBIO'."
                  />
                </Grid>
              </Grid>

              <Grid container spacing={3}>
                <Grid item xs={12} md={5}>
                  <Paper
                    elevation={2}
                    sx={{ p: 3, borderRadius: 2, height: "100%", display: "flex", flexDirection: "column" }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        Termómetro: Intención de Voto
                      </Typography>
                    </Box>
                    <Box
                      sx={{
                        flexGrow: 1,
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        minHeight: 350,
                      }}
                    >
                      {intencionData.length > 0 ? (
                        <PieChart
                          series={[
                            {
                              data: intencionData,
                              innerRadius: 70,
                              paddingAngle: 3,
                              cornerRadius: 5,
                              valueFormatter: (v) => `${v.value}%`,
                            },
                          ]}
                          height={300}
                          slotProps={{
                            legend: { direction: "column", position: { vertical: "middle", horizontal: "right" } },
                          }}
                        />
                      ) : (
                        <Typography color="textSecondary" fontStyle="italic">
                          Datos insuficientes.
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={7}>
                  <Paper
                    elevation={2}
                    sx={{ p: 3, borderRadius: 2, height: "100%", display: "flex", flexDirection: "column" }}
                  >
                    <Box display="flex" alignItems="center" justifyContent="center" mb={1}>
                      <Typography variant="h6" color="primary" fontWeight="bold">
                        Matriz de Dolores Ciudadanos
                      </Typography>
                    </Box>
                    <Box sx={{ flexGrow: 1, minHeight: 350 }}>
                      {doloresData.length > 0 ? (
                        <BarChart
                          dataset={doloresData}
                          yAxis={[{ scaleType: "band", dataKey: "label" }]}
                          series={[{ dataKey: "value", color: "#ed6c02", valueFormatter: (v) => `${v} pts` }]}
                          layout="horizontal"
                          height={350}
                          margin={{ left: 190, right: 30, top: 20, bottom: 20 }}
                        />
                      ) : (
                        <Typography color="textSecondary" fontStyle="italic">
                          Datos insuficientes.
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* ========================================================= */}
          {/* FASE 2: TRACKING Y SIMULADOR                              */}
          {/* ========================================================= */}
          {activeTab === 1 && (
            <Box>
              <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
                Radiografía de la Campaña en Curso
              </Typography>
              <Grid container spacing={3} mb={5}>
                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        Share of Voice (Mix de Medios)
                      </Typography>
                      <Tooltip
                        title="Mide por qué canales se está enterando el electorado de nuestras propuestas."
                        placement="top"
                      >
                        <IconButton size="small">
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box sx={{ height: 250, display: "flex", justifyContent: "center", alignItems: "center" }}>
                      {mixMediosData.length > 0 ? (
                        <PieChart
                          series={[
                            {
                              data: mixMediosData,
                              innerRadius: 0,
                              paddingAngle: 1,
                              valueFormatter: (v) => `${v.value}%`,
                            },
                          ]}
                          height={220}
                          slotProps={{
                            legend: { direction: "column", position: { vertical: "middle", horizontal: "right" } },
                          }}
                        />
                      ) : (
                        <Typography color="textSecondary">No hay datos suficientes</Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Paper elevation={2} sx={{ p: 3, borderRadius: 2 }}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Typography variant="subtitle1" fontWeight="bold" color="primary">
                        Índice de Firmeza de Voto
                      </Typography>
                      <Tooltip title="Voto Duro (inmune a ataques) vs Voto Blando (persuadible)." placement="top">
                        <IconButton size="small">
                          <InfoIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <Box sx={{ height: 250 }}>
                      {firmezaData.length > 0 ? (
                        <BarChart
                          dataset={firmezaData}
                          xAxis={[{ scaleType: "band", dataKey: "label" }]}
                          series={[{ dataKey: "value", color: "#2E7D32", valueFormatter: (v) => `${v}%` }]}
                          height={250}
                        />
                      ) : (
                        <Typography color="textSecondary">No hay datos suficientes</Typography>
                      )}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>

              <Divider sx={{ my: 4 }}>
                <Typography
                  variant="h6"
                  color="textSecondary"
                  fontWeight="bold"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <OnlinePrediction sx={{ mr: 1 }} /> Módulo de Simulación Predictiva
                </Typography>
              </Divider>

              {/* ... (Controles del simulador idénticos al código original para no hacer el archivo excesivamente largo aquí) ... */}
              {/* Nota: Mantuve la estructura exacta del Grid de simulador que ya tenía */}
              <Grid container spacing={4}>
                <Grid item xs={12} md={5}>
                  <Paper elevation={3} sx={{ p: 4, borderRadius: 3, bgcolor: "white", borderTop: "5px solid #ed6c02" }}>
                    <Typography variant="h6" color="primary" fontWeight="bold" gutterBottom>
                      Controles de Simulación
                    </Typography>
                    <Box mb={3}>
                      <TextField
                        select
                        fullWidth
                        label="Territorio Activo"
                        value={simCanton}
                        onChange={(e) => setSimCanton(e.target.value)}
                        size="small"
                        sx={{ mb: 3 }}
                      >
                        <MenuItem value="Daule">Cantón Daule</MenuItem>
                        <MenuItem value="Sucre">Cantón Sucre (Bahía)</MenuItem>
                      </TextField>
                      <TextField
                        select
                        fullWidth
                        label="Canal Dominante de la Semana"
                        value={simCanal}
                        onChange={(e) => setSimCanal(e.target.value)}
                        size="small"
                        sx={{ mb: 1 }}
                      >
                        <MenuItem value="Redes Sociales">Redes Sociales (Base)</MenuItem>
                        <MenuItem value="Recorridos de Calle">Recorridos de Calle (Alto Impacto)</MenuItem>
                        <MenuItem value="Boca a Boca">Boca a Boca (Orgánico)</MenuItem>
                        <MenuItem value="Radio Local">Radio Local (Tradicional)</MenuItem>
                      </TextField>
                      <Typography variant="caption" color="textSecondary">
                        Seleccione dónde invertirá el mayor presupuesto esta semana.
                      </Typography>
                    </Box>
                    <Box mb={3}>
                      <Typography variant="body2" fontWeight="bold" gutterBottom>
                        Recordación de Mensaje: {simRecall}%
                      </Typography>
                      <Slider
                        value={simRecall}
                        onChange={(e, val) => setSimRecall(val)}
                        valueLabelDisplay="auto"
                        color="primary"
                      />
                      <Typography variant="caption" color="textSecondary">
                        Partiendo de Base: <b>{data.tasaPenetracion || 0}%</b>
                      </Typography>
                    </Box>
                    <Box mb={3}>
                      <Typography variant="body2" fontWeight="bold" gutterBottom color="error">
                        Antivoto del Candidato: {simAntivoto}%
                      </Typography>
                      <Slider
                        value={simAntivoto}
                        onChange={(e, val) => setSimAntivoto(val)}
                        valueLabelDisplay="auto"
                        sx={{ color: "#d32f2f" }}
                      />
                      <Typography variant="caption" color="textSecondary">
                        Partiendo de Base: <b>{data.pctAntivoto || 0}%</b>
                      </Typography>
                    </Box>
                    <Box mb={1}>
                      <Typography variant="body2" fontWeight="bold" gutterBottom color="success.main">
                        Voto Firme (Leal): {simVotoFirme}%
                      </Typography>
                      <Slider
                        value={simVotoFirme}
                        onChange={(e, val) => setSimVotoFirme(val)}
                        valueLabelDisplay="auto"
                        color="success"
                      />
                    </Box>
                  </Paper>
                </Grid>

                <Grid item xs={12} md={7}>
                  <Grid container spacing={2}>
                    <Grid item xs={12}>
                      <Paper
                        elevation={3}
                        sx={{
                          p: 3,
                          borderRadius: 3,
                          borderLeft: techoElectoral < 50 ? "6px solid #d32f2f" : "6px solid #196C87",
                        }}
                      >
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="h6" fontWeight="bold" color="textSecondary">
                            1. Techo Electoral Máximo
                          </Typography>
                          <Tooltip title="Fórmula: 100% - Antivoto. Representa la máxima cantidad de votos posibles a captar.">
                            <InfoIcon color="action" />
                          </Tooltip>
                        </Box>
                        <Typography
                          variant="h3"
                          fontWeight="bold"
                          color={techoElectoral < 50 ? "error" : "primary"}
                          mb={1}
                        >
                          {techoElectoral}%
                        </Typography>
                        <LinearProgress
                          variant="determinate"
                          value={techoElectoral}
                          color={techoElectoral < 50 ? "error" : "primary"}
                          sx={{ height: 10, borderRadius: 5, mb: 2 }}
                        />
                        {techoElectoral < 50 && (
                          <Alert severity="error" variant="filled" sx={{ borderRadius: 2 }}>
                            <b>ESTADO CRÍTICO:</b> El techo electoral ha perforado el límite de viabilidad del 50%. Es
                            imposible ganar si no se reduce el Antivoto.
                          </Alert>
                        )}
                        {techoElectoral >= 50 && (
                          <Typography variant="body2" color="textSecondary">
                            El candidato es electoralmente viable. Hay terreno para crecer.
                          </Typography>
                        )}
                      </Paper>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Card elevation={3} sx={{ borderRadius: 3, height: "100%" }}>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">
                              2. ÍNDICE DE PERSUABILIDAD
                            </Typography>
                            <Psychology color="primary" />
                          </Box>
                          <Typography variant="h4" fontWeight="bold" color="primary" my={1}>
                            {persuabilidad}%
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            Fórmula: 100% - Voto Firme. Es la cantidad de electores "blandos" que aún pueden cambiar su
                            decisión.
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                      <Card elevation={3} sx={{ borderRadius: 3, height: "100%" }}>
                        <CardContent>
                          <Box display="flex" justifyContent="space-between">
                            <Typography variant="subtitle2" fontWeight="bold" color="textSecondary">
                              3. EFICIENCIA DE CAMPAÑA
                            </Typography>
                            <Campaign color="secondary" />
                          </Box>
                          <Typography variant="h4" fontWeight="bold" color="secondary" my={1}>
                            {eficienciaCampana}%
                          </Typography>
                          <Typography variant="caption" color="textSecondary">
                            El canal "{simCanal}" tiene un multiplicador de impacto de x{factorCanal}.
                          </Typography>
                        </CardContent>
                      </Card>
                    </Grid>
                  </Grid>
                </Grid>
              </Grid>
            </Box>
          )}

          {/* ========================================================= */}
          {/* FASE 3: WAR ROOM TÁCTICO                                  */}
          {/* ========================================================= */}
          {activeTab === 2 && (
            <Box>
              <Typography variant="h5" color="primary" fontWeight="bold" gutterBottom>
                Órdenes de Campo y Control Operativo
              </Typography>
              <Typography variant="body1" color="textSecondary" mb={4}>
                Listados de acción basados en cruce de datos territoriales.
              </Typography>

              <Grid container spacing={3}>
                {/* 1. EMBUDO DE ACTIVISMO */}
                <Grid item xs={12} lg={6}>
                  <Paper elevation={3} sx={{ p: 3, borderRadius: 3, height: "100%", borderTop: "4px solid #196C87" }}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <GroupAdd color="primary" sx={{ mr: 1, fontSize: 30 }} />
                      <Typography variant="h6" fontWeight="bold" color="primary">
                        1. Embudo de Activismo (CRM)
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary" mb={2}>
                      <b>¿Para qué sirve?</b> Entrega al Director de Campaña zonas exactas con bases orgánicas. Permite
                      activar centrales políticas en casas de simpatizantes en lugar de alquilar locales.
                    </Typography>
                    <TableContainer sx={{ maxHeight: 300, overflow: "auto" }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <b>Cantón/Zona</b>
                            </TableCell>
                            <TableCell>
                              <b>Compromiso</b>
                            </TableCell>
                            <TableCell align="right">
                              <b>Total</b>
                            </TableCell>
                            <TableCell align="right">
                              <b>Score</b>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {crmPoliticoData.length > 0 ? (
                            crmPoliticoData.map((row, idx) => (
                              <TableRow key={idx} hover>
                                <TableCell>
                                  {row.Canton} - {row.Zona_Electoral}
                                </TableCell>
                                <TableCell>{row.Tipo_Compromiso}</TableCell>
                                <TableCell align="right">{row.Cantidad_Simpatizantes}</TableCell>
                                <TableCell align="right">
                                  <Chip label={row.Score_Fuerza_Zona} size="small" color="primary" variant="outlined" />
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                Aún no hay datos tabulados
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>

                {/* 2. DÉFICIT LOGÍSTICO SUCRE */}
                <Grid item xs={12} lg={6}>
                  <Paper elevation={3} sx={{ p: 3, borderRadius: 3, height: "100%", borderTop: "4px solid #ed6c02" }}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <LocalShipping color="secondary" sx={{ mr: 1, fontSize: 30 }} />
                      <Typography variant="h6" fontWeight="bold" color="secondary">
                        2. Déficit Logístico "Día D" (Sucre)
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary" mb={2}>
                      <b>¿Para qué sirve?</b> Controla el presupuesto logístico. Cruza votantes duros rurales que
                      necesitan transporte contra voluntarios con vehículo. Solo se alquilan furgonetas donde hay
                      brecha.
                    </Typography>
                    <TableContainer sx={{ maxHeight: 300, overflow: "auto" }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <b>Parroquia / Recinto</b>
                            </TableCell>
                            <TableCell align="center">
                              <b>Req. Traslado</b>
                            </TableCell>
                            <TableCell align="center">
                              <b>Vehículos Propios</b>
                            </TableCell>
                            <TableCell align="center">
                              <b>Brecha</b>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {logisticaData.length > 0 ? (
                            logisticaData.map((row, idx) => (
                              <TableRow
                                key={idx}
                                hover
                                sx={{ bgcolor: row.Brecha_Logistica_Personas > 0 ? "#fff8e1" : "inherit" }}
                              >
                                <TableCell>
                                  {row.Parroquia} / {row.Recinto_Electoral}
                                </TableCell>
                                <TableCell align="center">{row.Requieren_Transporte}</TableCell>
                                <TableCell align="center">{row.Proveedores_Transporte}</TableCell>
                                <TableCell align="center">
                                  <Typography
                                    fontWeight="bold"
                                    color={row.Brecha_Logistica_Personas > 0 ? "error" : "success.main"}
                                  >
                                    {row.Brecha_Logistica_Personas > 0
                                      ? `Faltan mover ${row.Brecha_Logistica_Personas}`
                                      : "Cubierto"}
                                  </Typography>
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                Aún no hay datos tabulados
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>

                {/* 3. CONTROL ELECTORAL */}
                <Grid item xs={12} lg={6}>
                  <Paper elevation={3} sx={{ p: 3, borderRadius: 3, height: "100%", borderTop: "4px solid #d32f2f" }}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <Gavel color="error" sx={{ mr: 1, fontSize: 30 }} />
                      <Typography variant="h6" fontWeight="bold" color="error">
                        3. Cobertura Control Electoral
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary" mb={2}>
                      <b>¿Para qué sirve?</b> Arma el "Ejército de Cierre". Alerta en rojo las zonas de alta densidad
                      donde tenemos 0 delegados registrados para cuidar las actas. Dirige a la avanzada a reclutar.
                    </Typography>
                    <TableContainer sx={{ maxHeight: 300, overflow: "auto" }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <b>Cantón/Zona Electoral</b>
                            </TableCell>
                            <TableCell align="center">
                              <b>Confirmados</b>
                            </TableCell>
                            <TableCell align="center">
                              <b>Total Fuerza</b>
                            </TableCell>
                            <TableCell align="center">
                              <b>Alerta</b>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {controlData.length > 0 ? (
                            controlData.map((row, idx) => (
                              <TableRow
                                key={idx}
                                hover
                                sx={{ bgcolor: row.Total_Fuerza_Control === 0 ? "#ffebee" : "inherit" }}
                              >
                                <TableCell>
                                  {row.Canton} - {row.Zona_Electoral}
                                </TableCell>
                                <TableCell align="center">{row.Veedores_Confirmados}</TableCell>
                                <TableCell align="center" sx={{ fontWeight: "bold" }}>
                                  {row.Total_Fuerza_Control}
                                </TableCell>
                                <TableCell align="center">
                                  {row.Total_Fuerza_Control === 0 ? (
                                    <Chip label="ZONA VULNERABLE" color="error" size="small" />
                                  ) : (
                                    <Chip label="VIGILADA" color="success" size="small" variant="outlined" />
                                  )}
                                </TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={4} align="center">
                                Aún no hay datos tabulados
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>

                {/* 4. MICRO-NECESIDADES */}
                <Grid item xs={12} lg={6}>
                  <Paper elevation={3} sx={{ p: 3, borderRadius: 3, height: "100%", borderTop: "4px solid #2E7D32" }}>
                    <Box display="flex" alignItems="center" mb={1}>
                      <RecordVoiceOver color="success" sx={{ mr: 1, fontSize: 30 }} />
                      <Typography variant="h6" fontWeight="bold" sx={{ color: "#2E7D32" }}>
                        4. Radar Micro-Local (Guion Candidato)
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="textSecondary" mb={2}>
                      <b>¿Para qué sirve?</b> Mapea el problema #1 cuadra por cuadra en Daule. Es el guion del candidato
                      para que al recorrer un barrio no hable en general, sino de la demanda específica.
                    </Typography>
                    <TableContainer sx={{ maxHeight: 300, overflow: "auto" }}>
                      <Table stickyHeader size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              <b>Barrio / Etapa</b>
                            </TableCell>
                            <TableCell>
                              <b>Mensaje Clave (Demanda #1)</b>
                            </TableCell>
                            <TableCell align="right">
                              <b>Frecuencia</b>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {microData.length > 0 ? (
                            microData.map((row, idx) => (
                              <TableRow key={idx} hover>
                                <TableCell>
                                  <b>{row.Barrio_Etapa}</b>
                                </TableCell>
                                <TableCell sx={{ color: "#2E7D32", fontWeight: "500" }}>
                                  {row.Mensaje_Clave_Recomendado}
                                </TableCell>
                                <TableCell align="right">{row.Votantes_Demandantes}</TableCell>
                              </TableRow>
                            ))
                          ) : (
                            <TableRow>
                              <TableCell colSpan={3} align="center">
                                Aún no hay datos tabulados
                              </TableCell>
                            </TableRow>
                          )}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </Paper>
                </Grid>
              </Grid>
            </Box>
          )}
        </StyledRoot>
      </div>
    </ThemeProvider>
  )
}

export default DashboardPoliticoIndex
